import { Formik } from 'formik';
import { FC } from 'react';
import * as yup from 'yup';
import { useFormContext } from '../../../../../contexts/FormContext';
import { ContentImproverInput, FormType } from '../../../../../types/FormTypes';
import { ListenToFormikValues } from '../../../../shared/formik/ListenToFormikValues';
import { GenerateResponseButtons } from '../GenerateResponseButtons';

import formStyles from '../formStyles.module.css';
import { IntentSelectionInput } from '../formInputs/IntentInput';
import { ContentImproverIntents } from '../../../../../types/FormOptions.type';
import { OriginalCopyTextarea } from '../formInputs/OriginalCopyTextarea';

const ContentImproverSchema = yup.object<
  Record<keyof ContentImproverInput, yup.AnySchema>
>({
  intent: yup.string().required('Please select the intent of your content'),
  originalCopy: yup
    .string()
    .required('Please paste the copy that you would like to improve'),
  prevResponse: yup.string().notRequired(),
  advancedInputs: yup.boolean().notRequired(),
});

export const ContentImproverForm: FC = () => {
  const {
    contentImproverInput,
    setContentImproverInput,
    setCanKeepGoing,
    loading,
    submitToolkitForm,
  } = useFormContext();

  return (
    <Formik
      initialValues={contentImproverInput}
      onSubmit={async (values, actions) => {
        await submitToolkitForm({
          values,
          actions,
          formType: FormType.CONTENT_IMPROVER,
        });
      }}
      validationSchema={ContentImproverSchema}
    >
      {(props) => (
        <>
          <div className={formStyles['form-container']}>
            <ListenToFormikValues
              callBackFunc={(values) => {
                setContentImproverInput(values as ContentImproverInput);
                setCanKeepGoing(false);
              }}
            />
            <div>
              <IntentSelectionInput
                formikProps={props}
                disabled={loading}
                title="How would you like to improve your copy?"
                subtitle="Please select the desired improvement."
                defaultOptions={Object.keys(ContentImproverIntents)}
                defaultOptionIcons={[
                  '/icons/GageBlack.svg',
                  '/icons/TargetArrowBlack.svg',
                  '/icons/DollarSignBlack.svg',
                ].map((src) => ({
                  src,
                  height: 16,
                  width: 16,
                }))}
              />
            </div>
            <div>
              <OriginalCopyTextarea
                formikProps={props}
                disabled={loading}
                title="What copy do you want to improve?"
                subtitle="Please provide the copy that you want to improve."
                placeholder="Copy that needs improvement"
              />
            </div>
          </div>
          <GenerateResponseButtons
            onSubmit={() => {
              props.handleSubmit();
            }}
          />
        </>
      )}
    </Formik>
  );
};
